import React, { FC, useEffect } from "react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useParams, useNavigate } from "react-router-dom";
import { observer, useLocalObservable } from "mobx-react";
import { Button, DatePicker, Form, Input, Modal, Radio, Result, Select, Skeleton, Tag, notification } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { isNativeApp } from "../../native/native";
import { openNativeCheckout } from "../../native/checkout";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault("Europe/Rome");

interface Props {
  store: any;
}

const SectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-base font-bold mt-8 mb-4 pb-2 border-b border-solid border-neutral-200">{children}</div>
);

const Registration: FC<Props> = observer((props) => {
  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { eventId } = useParams();

  // Drives the order-summary bar; updates as the runner picks a distance.
  const selectedSubEventRef = Form.useWatch("subEvent", form);

  const state = useLocalObservable(() => ({
    event: null,
    loading: true,
    couponCode: "",
    couponStatus: "idle" as "idle" | "validating" | "valid" | "invalid",
    couponPreview: null as null | {
      discountType: "percentage" | "fixed" | "free";
      discountValue: number;
      originalAmount: number;
      discountAmount: number;
      finalAmount: number;
      free: boolean;
    },
    couponError: "",
  }));

  useEffect(() => {
    // Anything in here is fired on component mount.
    state.event = props.store.events.find((event: any) => event._id === eventId);

    const getEvent = async () => {
      try {
        state.event = await props.store.getData(`events/${eventId}`);
      } catch (err) {
        state.event = null;
      } finally {
        state.loading = false;
      }
    };

    if (!state.event) {
      getEvent();
    } else {
      state.loading = false;
    }
  }, [props.store, eventId, state]);

  let event = state.event;

  if (event) {
    event.notifyLateEntries = event.lateEntries && event.entriesLeft !== null && event.entriesLeft <= 0;
  }

  // Pricing depends on the chosen sub-event and the email; invalidate an applied coupon when either changes.
  const handleValuesChange = (changed: any) => {
    if (("subEvent" in changed || "email" in changed) && state.couponStatus !== "idle") {
      state.couponStatus = "idle";
      state.couponPreview = null;
      state.couponError = state.couponCode ? "Details changed — please re-apply your coupon." : "";
    }
  };

  const handleApplyCoupon = async () => {
    const code = (state.couponCode || "").trim();
    if (!code) {
      state.couponStatus = "invalid";
      state.couponPreview = null;
      state.couponError = "Please enter a coupon code.";
      return;
    }

    // Bundle events price off a single bundlePrice; normal events need a sub-event selected first.
    const subEvent = event.isBundle ? null : form.getFieldValue("subEvent");
    if (!event.isBundle && !subEvent) {
      state.couponStatus = "invalid";
      state.couponPreview = null;
      state.couponError = "Please select an event before applying a coupon.";
      return;
    }

    state.couponStatus = "validating";
    state.couponError = "";

    const result = await props.store.validateCoupon({
      code,
      eventId: event._id,
      subEvent,
      email: form.getFieldValue("email"),
    });

    if (result.success) {
      state.couponPreview = result.data;
      state.couponStatus = "valid";
      state.couponError = "";
    } else {
      state.couponPreview = null;
      state.couponStatus = "invalid";
      state.couponError = result.error || "This coupon code is not valid.";
    }
  };

  const handleRegisterEvent = async (formValues) => {
    formValues.dob = dayjs(formValues.dob).format("YYYY-MM-DD");

    // Only attach a code that was actually validated to "valid".
    if (state.couponStatus === "valid" && state.couponCode) {
      formValues.couponCode = state.couponCode.trim();
    }

    const result = await props.store.postData(`events/${event._id}/register`, formValues);

    // Registration failed (includes 400 if the coupon went invalid between Apply and submit).
    if (!(result?.data?.registrationIds && result.data.registrationIds.length > 0)) {
      api.error({
        message: "Registration Failed",
        description: result?.data?.message || "Please try again and if problem persists contact us on our FB Events Page.",
      });

      // If the server rejected the coupon, reset the preview so the user can re-apply.
      if (result?.status === 400) {
        state.couponStatus = "idle";
        state.couponPreview = null;
      }

      console.error("Could not create registration");
      return;
    }

    // FREE coupon: server already marked the entry paid + emailed the confirmation. Skip Stripe.
    if (result.data.free === true) {
      window.location.href = result.data.bookingRef ? `/thankyou?ref=${encodeURIComponent(result.data.bookingRef)}` : "/thankyou";
      return;
    }

    // Discounted or full price -> Stripe. For discounted entries the server recomputes the amount
    // from the stored registrations; for non-coupon entries it resolves the price server-side.
    const checkoutSession = await props.store.postData("payments/create", {
      registrationIds: result.data.registrationIds,
      eventId: event._id,
      email: formValues.email,
      // App requests get {url, bookingRef} back (web keeps the plain URL string)
      // and the API swaps the unroutable WebView origin for the public site URL.
      ...(isNativeApp() ? { platform: "app" } : {}),
    });

    if (!checkoutSession || checkoutSession.status >= 400) {
      api.error({
        message: "Payment Error",
        description: "We couldn't start the checkout. Please try again.",
      });
      console.error(checkoutSession?.data?.message);
    } else if (isNativeApp()) {
      const { url, bookingRef } = checkoutSession.data;
      const outcome = await openNativeCheckout(url, async () => {
        const status = await props.store.postData("payments/status", {
          registrationIds: result.data.registrationIds,
          eventId: event._id,
        });
        return status?.data?.paid === true;
      });

      // The success deep link (layer 1) may have already navigated us away.
      if (window.location.pathname.startsWith("/thankyou")) return;

      if (outcome === "paid") {
        window.location.href = `/thankyou?ref=${encodeURIComponent(bookingRef)}`;
      } else {
        api.warning({
          message: "Checkout not completed",
          description:
            "If you completed the payment you'll receive a confirmation email shortly — otherwise you can try again.",
        });
      }
    } else {
      window.location.href = checkoutSession.data;
    }
  };

  const getResidenceLabel = () => {
    if (event.locationRef === "zebbug") {
      return "Żebbug Resident";
    }

    if (event.locationRef === "zurrieq") {
      return "Żurrieq Resident";
    }
  };

  const renderBundleInfo = () => {
    return (
      <div className="bg-primary/5 border border-solid border-primary/20 rounded-lg p-4 font-semibold mb-6">
        Bundle Package will entitle you to the 5K distance of the bundled competitions at a discounted price of{" "}
        {event.bundlePrice?.fee}
      </div>
    );
  };

  const renderSizeInfoModal = () => {
    Modal.info({
      title: "Shirt Size Information",
      content: (
        <div>
          <img src={event.shirtSizeInfoImage} alt="Shirt Size Info" className="w-full h-auto" />
        </div>
      ),
      onOk() {},
    });
  };

  const renderSubEventsFormItem = () => {
    return (
      <Form.Item
        name="subEvent"
        rules={[
          {
            required: true,
            message: "Please select an event!",
          },
        ]}
      >
        <Radio.Group className="w-full flex flex-col gap-2">
          {event.subEvents.map((subEvent: any) => {
            if (event.lateEntries && subEvent.restrictLateEnries) {
              return null;
            }

            return (
              <Radio
                key={subEvent.ref}
                value={subEvent.ref}
                className="w-full m-0 items-center border border-solid border-neutral-200 rounded-lg p-3 hover:border-primary transition-colors [&>span:last-child]:w-full [&>span:last-child]:pr-0"
              >
                <span className="flex w-full items-center justify-between gap-3">
                  <span>
                    <span className="block font-bold">{subEvent.ref}</span>
                    {subEvent.note && <span className="block text-sm text-neutral-500">{subEvent.note}</span>}
                  </span>
                  <span className="font-bold shrink-0">{subEvent.fee}</span>
                </span>
              </Radio>
            );
          })}
        </Radio.Group>
      </Form.Item>
    );
  };

  const renderCouponField = () => {
    const p = state.couponPreview;

    return (
      <div className="mb-4">
        <Form.Item style={{ marginBottom: 8 }}>
          <div className="flex gap-2">
            <Input
              value={state.couponCode}
              placeholder="Enter coupon code"
              onChange={(e) => (state.couponCode = e.target.value.toUpperCase())}
              onPressEnter={(e) => {
                e.preventDefault();
                handleApplyCoupon();
              }}
            />
            <Button htmlType="button" onClick={handleApplyCoupon} loading={state.couponStatus === "validating"}>
              Apply
            </Button>
          </div>
        </Form.Item>

        {state.couponStatus === "invalid" && state.couponError && (
          <div className="text-red-600 text-sm mb-2">{state.couponError}</div>
        )}
        {state.couponStatus === "idle" && state.couponError && (
          <div className="text-amber-600 text-sm mb-2">{state.couponError}</div>
        )}

        {state.couponStatus === "valid" && p && (
          <div className="text-sm bg-green-50 border border-solid border-green-300 rounded-md p-2 mb-2">
            {p.free ? (
              <span className="font-bold text-green-700">Coupon applied — entry is FREE (€0.00)</span>
            ) : (
              <span className="font-bold text-green-700">
                Coupon applied — <span className="line-through text-gray-500">€{p.originalAmount.toFixed(2)}</span>{" "}
                €{p.finalAmount.toFixed(2)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Order summary breakdown. Basis: the coupon-adjusted amount when a coupon is
  // applied, else the picked distance's numeric price. A pass_on booking fee
  // (event.publicBookingFee, published by the API only for pass_on events) is
  // shown on top — mirrors the server's fee math for display; the server remains
  // the authority at registration time.
  const getSummaryBreakdown = (): { subtotal: number | null; fee: number; total: number | null; free: boolean } => {
    let subtotal: number | null = null;
    let free = false;

    if (state.couponStatus === "valid" && state.couponPreview) {
      subtotal = state.couponPreview.finalAmount;
      free = state.couponPreview.free;
    } else if (event.isBundle) {
      subtotal = typeof event.bundlePrice?.price === "number" ? event.bundlePrice.price : null;
    } else {
      const subEvent = event.subEvents?.find((se: any) => se.ref === selectedSubEventRef);
      subtotal = typeof subEvent?.price === "number" ? subEvent.price : null;
    }

    const pub = event.publicBookingFee;
    let fee = 0;
    if (pub && subtotal !== null && subtotal > 0 && !free) {
      fee = round2((subtotal * (pub.percentage || 0)) / 100 + (pub.fixed || 0));
    }

    return { subtotal, fee, total: subtotal !== null ? round2(subtotal + fee) : null, free };
  };

  const renderSummaryRow = () => {
    const { subtotal, fee, total, free } = getSummaryBreakdown();
    const money = (n: number) => `€${n.toFixed(2)}`;

    return (
      <div className="flex items-center justify-between gap-3">
        <div>
          {fee > 0 && subtotal !== null && (
            <div className="text-xs text-neutral-500">
              Entry {money(subtotal)} + booking fee {money(fee)}
            </div>
          )}
          <div className="text-xs text-neutral-500">Total</div>
          <div className="text-lg font-extrabold">{free ? "FREE" : total !== null ? money(total) : "—"}</div>
        </div>
        <Button type="primary" size="large" onClick={() => form.submit()}>
          Register
        </Button>
      </div>
    );
  };

  const renderRegisterForm = () => {
    return (
      <div className="max-w-2xl mx-auto px-4 mt-6 pb-28 md:pb-10">
        <div className="bg-white rounded-2xl shadow-sm p-5 md:p-8">
          <Form layout="vertical" form={form} name="register" onFinish={handleRegisterEvent} onValuesChange={handleValuesChange} scrollToFirstError>
            {event.isBundle && renderBundleInfo()}

            <SectionTitle>Your details</SectionTitle>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  type: "email",
                  transform: (value) => value.trim(),
                  message: "The input is not valid E-mail!",
                },
                {
                  required: true,
                  message: "Please input your Email!",
                },
              ]}
            >
              <Input />
            </Form.Item>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  {
                    required: true,
                    message: "Please input your First Name!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  {
                    required: true,
                    message: "Please input your Last Name!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </div>
            <Form.Item
              name="gender"
              label="Gender"
              rules={[
                {
                  required: true,
                  message: "Please input Gender!",
                },
              ]}
            >
              <Radio.Group>
                <Radio value="M">Male</Radio>
                <Radio value="F">Female</Radio>
              </Radio.Group>
            </Form.Item>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
              <Form.Item
                name="dob"
                label="Date of Birth"
                rules={[
                  {
                    type: "object" as const,
                    required: true,
                    message: "Please input your Date of Birth!",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  showToday={false}
                  placeholder={"DD/MM/YYYY"}
                  disabledDate={(current) => {
                    // Disable future dates and dates more than 100 years ago
                    const today = dayjs();
                    const hundredYearsAgo = dayjs().subtract(100, "years");
                    return current && (current > today || current < hundredYearsAgo);
                  }}
                  changeOnBlur={true}
                  allowClear={true}
                  style={{ width: "100%" }}
                  onKeyDown={(e) => {
                    // Allow backspace, delete, tab, escape, enter, and arrow keys
                    if (
                      [8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                      (e.keyCode === 65 && e.ctrlKey === true) ||
                      (e.keyCode === 67 && e.ctrlKey === true) ||
                      (e.keyCode === 86 && e.ctrlKey === true) ||
                      (e.keyCode === 88 && e.ctrlKey === true)
                    ) {
                      return;
                    }
                    // Allow only numbers and forward slash for DD/MM/YYYY format
                    if ((e.keyCode < 48 || e.keyCode > 57) && e.keyCode !== 191) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="contactNo"
                label="Contact No."
                rules={[
                  {
                    required: true,
                    message: "Please input your contact number!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </div>

            <Form.Item name="club" label="Club (if any)" initialValue={"NA"}>
              <Select placeholder="Select a club" onChange={(val) => (props.store.selectedClub = val)} showSearch>
                <Select.Option value="NA">No Club</Select.Option>
                <Select.Option value="Zurrieq Wolves AC">Zurrieq Wolves AC</Select.Option>
                <Select.Option value="Athleta Pembroke">Athleta Pembroke</Select.Option>
                <Select.Option value="Evolve Endurance Club">Evolve Endurance Club</Select.Option>
                <Select.Option value="Mellieha AC">Mellieha AC</Select.Option>
                <Select.Option value="La Salle">La Salle</Select.Option>
                <Select.Option value="Rush AC">Rush AC</Select.Option>
                <Select.Option value="Savio AC">Savio AC</Select.Option>
                <Select.Option value="Birkirkara St.Joseph">Birkirkara St.Joseph</Select.Option>
                <Select.Option value="Ladies Running Club">Ladies Running Club</Select.Option>
                <Select.Option value="Athletix Gozo">Athletix Gozo</Select.Option>
                <Select.Option value="St.Patricks AC">St.Patricks AC</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </Form.Item>

            {props.store.selectedClub === "Other" && (
              <Form.Item name="otherClub" label="Club Name">
                <Input />
              </Form.Item>
            )}

            {/* show resident only on zebbug and zurrieq races and if bundle show dropdown with multiple options */}
            {event.isBundle ? (
              <Form.Item name="resident" label="Resident Status" initialValue="none">
                <Select placeholder="Select resident status">
                  <Select.Option value="none">None</Select.Option>
                  <Select.Option value="zebbug">Żebbug Resident</Select.Option>
                  <Select.Option value="zurrieq">Żurrieq Resident</Select.Option>
                </Select>
              </Form.Item>
            ) : (
              event.locationRef && (
                <Form.Item name="resident" label={getResidenceLabel()}>
                  <Radio.Group>
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </Radio.Group>
                </Form.Item>
              )
            )}

            {(!event.isBundle && event.subEvents && event.subEvents.length > 0) || event.enableShirtSize ? (
              <SectionTitle>Race options</SectionTitle>
            ) : null}

            {!event.isBundle && event.subEvents && event.subEvents.length > 0 && renderSubEventsFormItem()}

            {event.enableShirtSize && (
              <Form.Item
                name="shirtSize"
                label={
                  <span className="flex items-center gap-2">
                    Shirt Size
                    {event.shirtSizeInfoImage && (
                      <Button
                        type="link"
                        size="small"
                        className="px-0"
                        icon={<InfoCircleOutlined />}
                        onClick={renderSizeInfoModal}
                      >
                        Size chart
                      </Button>
                    )}
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please input shirt size!",
                  },
                ]}
              >
                <Radio.Group className="w-full">
                  <div className="flex flex-wrap gap-x-5 gap-y-2 mb-2">
                    <Radio value="4">4-6</Radio>
                    <Radio value="6">6-8</Radio>
                    <Radio value="8">8-10</Radio>
                    <Radio value="10">10-12</Radio>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    <Radio value="S">S</Radio>
                    <Radio value="M">M</Radio>
                    <Radio value="L">L</Radio>
                    <Radio value="XL">XL</Radio>
                  </div>
                </Radio.Group>
              </Form.Item>
            )}

            <SectionTitle>Coupon</SectionTitle>
            {renderCouponField()}

            <SectionTitle>Disclaimer</SectionTitle>
            <div className="bg-neutral-50 text-sm text-neutral-600 mb-4 p-4 border border-solid border-neutral-200 rounded-lg">
              By submitting form, I declare that I will abide by the eligibility and competition rules of Athletics Malta
              and Zurrieq Wolves Athletic Events. I accept that the organisers, helpers, sponsors or any other member of
              the organization, will not be held liable for any loss, damage or injury I may sustain as a consequence of
              my participation in the event. I declare that I have trained sufficiently for the event and that a medical
              doctor has monitored my physical condition over the past few months and that I am fit to run this race.
              Additionally, I hereby declare that the above particulars are complete, correct in every detail.
            </div>
            {event.notifyLateEntries && (
              <div className="font-semibold text-red-700 mb-4">Entry not eligible for medal and goody bag</div>
            )}

            {/* Desktop: inline summary + submit. Mobile: the sticky bar below stands in. */}
            <div className="hidden md:block border-t border-solid border-neutral-200 pt-4">{renderSummaryRow()}</div>
          </Form>
        </div>

        {/* Mobile sticky order summary */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-0 border-t border-solid border-neutral-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {renderSummaryRow()}
        </div>
      </div>
    );
  };

  const renderStatusCard = (title: string, subTitle: string) => (
    <div className="max-w-xl mx-auto px-4 my-8 md:my-12">
      <Result
        className="bg-white rounded-2xl shadow-sm"
        status="info"
        title={title}
        subTitle={subTitle}
        extra={
          <Button type="primary" onClick={() => navigate("/")}>
            Back to Events
          </Button>
        }
      />
    </div>
  );

  if (state.loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 mt-6">
        <div className="bg-white rounded-2xl shadow-sm p-5 md:p-8">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    );
  }

  if (!event) {
    return renderStatusCard("Event not found", "The event you are looking for doesn't exist or is no longer available.");
  }

  if (!event.active) {
    return renderStatusCard("This event is no longer active", "Applications for this event are closed.");
  }

  //check if event date expired
  if (dayjs(event.eventDate).isBefore(dayjs())) {
    return renderStatusCard("Event Expired", "This event has already taken place.");
  }

  //check if limit has been reached
  if (event.entriesLeft !== null && event.entriesLeft <= 0 && !event.lateEntries) {
    return renderStatusCard("Event Sold Out", "All available places for this event have been taken.");
  }

  return (
    <div>
      {contextHolder}
      <div className="max-w-2xl mx-auto px-4 mt-6 md:mt-8">
        <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl md:text-2xl font-extrabold m-0">{event.name}</h1>
          {event.notifyLateEntries && (
            <Tag color="red" className="mr-0 font-semibold">
              Late Entry
            </Tag>
          )}
        </div>
      </div>
      {renderRegisterForm()}
    </div>
  );
});

export default Registration;
